import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import {takeEvery, getContext, call, put} from 'typed-redux-saga'
import Routine from 'src/models/Routine'
import {DataState} from 'src/types/dataState.type'
import {shouldLogUserOut} from '../configureStore'
import Api from '../../api/reactpt'

export type RoutinesState = {
  routines: Routine[]
  dataState: DataState
}
const initialState: RoutinesState = {
  routines: [],
  dataState: {state: 'unloaded'},
}

export const {actions, reducer} = createSlice({
  name: 'routines',
  initialState,
  reducers: {
    getRoutines: (_state: RoutinesState, _action: PayloadAction<void>) => {},
    setRoutines: (state: RoutinesState, action: PayloadAction<{routines: Routine[]}>) => {
      return {...state, routines: action.payload.routines}
    },
    saveRoutine: (
      _state: RoutinesState,
      _action: PayloadAction<{routine: Routine; imageFile: File | undefined}>,
    ) => {},
    deleteRoutine: (_state: RoutinesState, _action: PayloadAction<{routineId: string}>) => {},
    setDataState: (state: RoutinesState, action: PayloadAction<{dataState: DataState}>) => {
      const {dataState} = action.payload
      return {...state, dataState}
    },
    clear: (_state: RoutinesState) => {
      return {...initialState}
    },
  },
})

export const getRoutinesSaga = function* () {
  const api: Api = yield* getContext<Api>('api')
  yield* put(actions.setDataState({dataState: {state: 'loading'}}))
  try {
    const result = yield* call(api.getAllRoutines)
    yield* put(actions.setRoutines({routines: result}))
    yield* put(actions.setDataState({dataState: {state: 'loaded'}}))
  } catch (error) {
    if (shouldLogUserOut(error)) {
      throw error
    } else {
      yield* put(actions.setDataState({dataState: {state: 'unloaded', error}}))
    }
  }
}

export const deleteRoutineSaga = function* (action: ReturnType<typeof actions.deleteRoutine>) {
  const {routineId} = action.payload
  const api: Api = yield* getContext<Api>('api')
  yield* put(actions.setDataState({dataState: {state: 'loading'}}))
  try {
    yield* call(api.deleteRoutine, routineId)
    yield* put(actions.setDataState({dataState: {state: 'loaded'}}))
    yield* put(actions.getRoutines())
  } catch (error) {
    if (shouldLogUserOut(error)) {
      throw error
    } else {
      yield* put(actions.setDataState({dataState: {state: 'unloaded', error}}))
    }
  }
}

export const saveRoutineSaga = function* (action: ReturnType<typeof actions.saveRoutine>) {
  const {imageFile} = action.payload
  let updatedRoutine = {...action.payload.routine}
  const api = yield* getContext<Api>('api')
  yield* put(actions.setDataState({dataState: {state: 'loading'}}))
  try {
    if (imageFile) {
      // There's an image file, which means we need to request an upload URL.
      const {uploadURL, contentName} = yield* call(api.requestImageUpload, imageFile)

      // Now we can upload the image.
      yield* call(api.uploadImage, uploadURL, imageFile)

      // Save the content-name.
      updatedRoutine.imageContentName = contentName
    }
    if (updatedRoutine.id) {
      yield* call(api.updatePublicRoutine, updatedRoutine)
    } else {
      yield* call(api.createPublicRoutine, updatedRoutine)
    }
    yield* put(actions.getRoutines())
  } catch (error) {
    if (shouldLogUserOut(error)) {
      throw error
    } else {
      yield* put(actions.setDataState({dataState: {state: 'unloaded', error}}))
    }
  }
}

export const saga = function* () {
  yield* takeEvery(actions.getRoutines.type, getRoutinesSaga)
  yield* takeEvery(actions.saveRoutine.type, saveRoutineSaga)
  yield* takeEvery(actions.deleteRoutine.type, deleteRoutineSaga)
}
